import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Download, FileSpreadsheet, RotateCcw, Upload } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FormError } from '@/features/auth/auth-layout';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError, apiPost } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  downloadCsv,
  RECORD_LABELS,
  useImportFields,
  useImports,
  useUndoImport,
  type ImportField,
  type ImportJob,
  type RecordType,
} from './api';

const TYPES: RecordType[] = ['lead', 'contact', 'company', 'deal'];
const BATCH = 100;
const MAX_ROWS = 50_000;

type Sheet = { fileName: string; headers: string[]; rows: Record<string, string | number | boolean | null>[] };

const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** The field a column heading most likely means. */
function guess(header: string, fields: ImportField[], taken: Set<string>): string {
  const text = normalise(header);
  for (const field of fields) {
    if (taken.has(field.key)) continue;
    if (normalise(field.label) === text || field.key.toLowerCase() === text.replace(/ /g, '')) return field.key;
    if (field.aliases?.some((alias) => normalise(alias) === text)) return field.key;
  }
  return '';
}

async function readSheet(file: File): Promise<Sheet> {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.csv') || lower.endsWith('.txt')) {
    const Papa = (await import('papaparse')).default;
    const text = await file.text();
    const parsed = Papa.parse<Record<string, string>>(text.replace(/^﻿/, ''), {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (header) => header.trim(),
    });
    const headers = (parsed.meta.fields ?? []).filter(Boolean);
    return { fileName: file.name, headers, rows: parsed.data };
  }
  if (lower.endsWith('.xlsx')) {
    const readXlsxFile = (await import('read-excel-file')).default;
    const matrix = await readXlsxFile(file);
    const [head = [], ...body] = matrix;
    const headers = head.map((cell, index) => String(cell ?? '').trim() || `Column ${index + 1}`);
    const rows = body
      .filter((row) => row.some((cell) => cell !== null && String(cell).trim() !== ''))
      .map((row) =>
        Object.fromEntries(
          headers.map((header, index) => {
            const cell = row[index];
            const value = cell instanceof Date ? cell.toISOString().slice(0, 10) : (cell as string | number | boolean | null);
            return [header, value ?? null];
          }),
        ),
      );
    return { fileName: file.name, headers, rows };
  }
  throw new Error('Choose a .csv or .xlsx file (save older .xls files as .xlsx first)');
}

/**
 * The import wizard (HubSpot, AmoCRM): pick the file, match its columns to
 * fields, choose what happens to duplicates, and watch it go in. Every import
 * can be undone from the history.
 */
export function ImportPage() {
  const [params] = useSearchParams();
  const { can } = usePermission();
  const initial = (TYPES.includes(params.get('type') as RecordType) ? params.get('type') : 'lead') as RecordType;
  const [entityType, setEntityType] = useState<RecordType>(initial);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [duplicateMode, setDuplicateMode] = useState<'skip' | 'update' | 'create'>('skip');
  const [running, setRunning] = useState<{ done: number; job: ImportJob | null } | null>(null);
  const [result, setResult] = useState<ImportJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { data: fields = [] } = useImportFields(entityType);
  const { data: history = [], refetch } = useImports();
  const undo = useUndoImport();

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const parsed = await readSheet(file);
      if (!parsed.rows.length) throw new Error('That file has no rows under its heading row');
      if (parsed.rows.length > MAX_ROWS) throw new Error(`Up to ${MAX_ROWS.toLocaleString()} rows at a time — split the file`);
      const taken = new Set<string>();
      const next: Record<string, string> = {};
      for (const header of parsed.headers) {
        const key = guess(header, fields, taken);
        next[header] = key;
        if (key) taken.add(key);
      }
      setMapping(next);
      setSheet(parsed);
    } catch (failure) {
      setError((failure as Error).message);
    }
  };

  const mappedKeys = Object.values(mapping).filter(Boolean);
  const missing = fields
    .filter((field) => field.required)
    .filter((field) => !mappedKeys.includes(field.key) && !(field.key === 'firstName' && mappedKeys.includes('fullName')));

  const run = async () => {
    if (!sheet) return;
    setError(null);
    try {
      const job = await apiPost<ImportJob>('/imports', {
        entityType,
        fileName: sheet.fileName,
        mapping,
        duplicateMode,
        totalRows: sheet.rows.length,
      });
      setRunning({ done: 0, job });
      // Only the mapped columns travel; the rest of the sheet stays here.
      const columns = Object.entries(mapping).filter(([, key]) => key).map(([column]) => column);
      let latest = job;
      for (let offset = 0; offset < sheet.rows.length; offset += BATCH) {
        const rows = sheet.rows.slice(offset, offset + BATCH).map((row) => Object.fromEntries(columns.map((column) => [column, row[column] ?? null])));
        latest = await apiPost<ImportJob>(`/imports/${job.id}/rows`, { offset, rows });
        setRunning({ done: Math.min(offset + BATCH, sheet.rows.length), job: latest });
      }
      latest = await apiPost<ImportJob>(`/imports/${job.id}/finish`);
      setResult(latest);
      setSheet(null);
      toast.success(`Import finished: ${latest.createdCount} created, ${latest.updatedCount} updated`);
      void refetch();
    } catch (failure) {
      setError(failure instanceof ApiRequestError ? failure.message : 'The import stopped — what went in so far is kept and can be undone.');
      void refetch();
    } finally {
      setRunning(null);
    }
  };

  const sample = useMemo(() => sheet?.rows.slice(0, 3) ?? [], [sheet]);

  return (
    <div>
      <PageHeader
        title="Import & export"
        description="Bring in leads, contacts, companies or deals from Excel or CSV — columns are matched for you, duplicates are caught, and every import can be undone."
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'Import & export' }]}
      />

      <div className="grid gap-5 xl:grid-cols-[1fr_22rem]">
        <div className="space-y-5">
          {can('crm.import.create') && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">1. What are you importing?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {TYPES.map((type) => (
                    <button
                      key={type}
                      disabled={Boolean(running)}
                      onClick={() => {
                        setEntityType(type);
                        setSheet(null);
                        setResult(null);
                      }}
                      className={cn(
                        'rounded-xl border px-4 py-2 text-sm font-medium transition-colors',
                        entityType === type ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent',
                      )}
                    >
                      {RECORD_LABELS[type]}
                    </button>
                  ))}
                </div>
                <div
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    void choose(event.dataTransfer.files[0]);
                  }}
                  className="flex flex-col items-center rounded-2xl border-2 border-dashed border-primary/25 bg-primary/[0.03] px-6 py-10 text-center"
                >
                  <FileSpreadsheet className="h-10 w-10 text-primary/70" />
                  <p className="mt-2 font-semibold">{sheet ? sheet.fileName : 'Drop an .xlsx or .csv file here'}</p>
                  <p className="text-sm text-muted-foreground">
                    {sheet ? `${sheet.rows.length.toLocaleString()} rows · ${sheet.headers.length} columns` : 'The first row should hold the column names.'}
                  </p>
                  <input
                    ref={fileInput}
                    type="file"
                    accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    onChange={(event) => {
                      void choose(event.target.files?.[0]);
                      event.target.value = '';
                    }}
                  />
                  <Button className="mt-4" variant="outline" onClick={() => fileInput.current?.click()} disabled={Boolean(running)}>
                    <Upload /> {sheet ? 'Choose another file' : 'Choose file'}
                  </Button>
                </div>
                <FormError message={error} />
              </CardContent>
            </Card>
          )}

          {sheet && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">2. Match the columns</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Column in your file</TableHead>
                        <TableHead>Example</TableHead>
                        <TableHead className="w-64">Goes into</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sheet.headers.map((header) => (
                        <TableRow key={header}>
                          <TableCell className="font-medium">{header}</TableCell>
                          <TableCell className="max-w-[16rem] truncate text-xs text-muted-foreground">
                            {sample.map((row) => row[header]).filter((value) => value !== null && value !== '').slice(0, 2).join(' · ')}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={mapping[header] ?? ''}
                              onChange={(event) => setMapping((current) => ({ ...current, [header]: event.target.value }))}
                            >
                              <option value="">Don’t import</option>
                              {fields.map((field) => (
                                <option
                                  key={field.key}
                                  value={field.key}
                                  disabled={mappedKeys.includes(field.key) && mapping[header] !== field.key}
                                >
                                  {field.label}
                                  {field.required ? ' *' : ''}
                                </option>
                              ))}
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>When a row matches an existing record</Label>
                    <Select value={duplicateMode} onChange={(event) => setDuplicateMode(event.target.value as typeof duplicateMode)}>
                      <option value="skip">Skip it — keep what is there</option>
                      <option value="update">Update it with the filled cells</option>
                      <option value="create">Create a new record anyway</option>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {entityType === 'company'
                        ? 'Matched by company name.'
                        : entityType === 'deal'
                          ? 'Matched by deal name within the same company.'
                          : 'Matched by email, or by phone.'}
                    </p>
                  </div>
                </div>

                {missing.length > 0 && (
                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Still to map: {missing.map((field) => field.label).join(', ')}
                  </p>
                )}

                {running ? (
                  <div className="space-y-2">
                    <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-aurora transition-all" style={{ width: `${(running.done / sheet.rows.length) * 100}%` }} />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {running.done.toLocaleString()} of {sheet.rows.length.toLocaleString()} rows ·{' '}
                      {running.job?.createdCount ?? 0} created · {running.job?.updatedCount ?? 0} updated · {running.job?.failedCount ?? 0} failed
                    </p>
                  </div>
                ) : (
                  <Button onClick={run} disabled={missing.length > 0}>
                    <Upload /> Import {sheet.rows.length.toLocaleString()} {RECORD_LABELS[entityType].toLowerCase()}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {result && <ImportSummary job={result} />}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Import history</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing imported yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>File</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Result</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((job) => (
                        <TableRow key={job.id}>
                          <TableCell className="font-medium">
                            {job.fileName}
                            <p className="text-xs font-normal text-muted-foreground">{job.createdBy?.name}</p>
                          </TableCell>
                          <TableCell>{RECORD_LABELS[job.entityType]}</TableCell>
                          <TableCell className="text-xs">
                            {job.createdCount} new · {job.updatedCount} updated · {job.skippedCount} skipped
                            {job.failedCount ? <span className="text-destructive"> · {job.failedCount} failed</span> : null}
                            {job.status === 'UNDONE' && <Badge className="ms-2 bg-neutral-200 text-neutral-600">Undone</Badge>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatDateTime(job.createdAt)}</TableCell>
                          <TableCell className="text-end">
                            <div className="flex justify-end gap-1">
                              {job.failedCount > 0 && (
                                <Button size="sm" variant="ghost" onClick={() => void downloadCsv(`/imports/${job.id}/errors.csv`, 'import-errors.csv')}>
                                  <Download /> Errors
                                </Button>
                              )}
                              {job.status !== 'UNDONE' && job.createdCount > 0 && can('crm.import.delete') && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={undo.isPending}
                                  onClick={() => {
                                    if (!window.confirm(`Remove the ${job.createdCount} records this import created? They go to the recycle bin.`)) return;
                                    undo.mutate(job.id, { onSuccess: (data) => toast.success(`${data.removed} records removed`) });
                                  }}
                                >
                                  <RotateCcw /> Undo
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Export</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">Everything you can see, as a spreadsheet that opens in Excel.</p>
              {TYPES.filter((type) => can(`crm.${type}.export`)).map((type) => (
                <Button
                  key={type}
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => void downloadCsv(`/exports/${type}`, `${type}s.csv`).catch(() => toast.error('Export failed'))}
                >
                  <Download /> Export {RECORD_LABELS[type].toLowerCase()}
                </Button>
              ))}
              {!TYPES.some((type) => can(`crm.${type}.export`)) && (
                <p className="text-sm text-muted-foreground">Your role cannot export records.</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>One row per record, column names in the first row.</p>
              <p>Phone numbers like 050 123 4567 become +971 50 123 4567.</p>
              <p>Dates can be 16/10/2026 or 2026-10-16. Amounts can include “AED” and commas.</p>
              <p>Owner: put the user’s email address. Contacts and deals find their company by name, and create it when it is new.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ImportSummary({ job }: { job: ImportJob }) {
  return (
    <Card className="border-emerald-200">
      <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          <div>
            <p className="font-semibold">{job.fileName} is in</p>
            <p className="text-sm text-muted-foreground">
              {job.createdCount} created · {job.updatedCount} updated · {job.skippedCount} skipped as duplicates · {job.failedCount} failed
            </p>
          </div>
        </div>
        {job.failedCount > 0 && (
          <Button variant="outline" onClick={() => void downloadCsv(`/imports/${job.id}/errors.csv`, 'import-errors.csv')}>
            <Download /> Download the failed rows
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
